export type JdaFileRole = "site" | "shelf" | "articleMaster";

export interface JdaFileBuckets {
  site: File | null;
  shelf: File | null;
  articleMaster: File | null;
  unclassified: File[];
}

// The retailer's own JDA export always names these files this way (e.g.
// "JDA_SITE_20260620011532.csv") — checking the name first avoids reading
// file content for the common case.
const NAME_PATTERNS: { role: JdaFileRole; pattern: RegExp }[] = [
  { role: "site", pattern: /^JDA_SITE/i },
  { role: "shelf", pattern: /^JDA_SHELF/i },
  { role: "articleMaster", pattern: /^ARTICLE_MASTER/i },
];

export const classifyJdaFileByName = (fileName: string): JdaFileRole | null =>
  NAME_PATTERNS.find(({ pattern }) => pattern.test(fileName))?.role ?? null;

// Fallback for a renamed file: the 3 file types have unambiguous header shapes
// — Article Master is pipe-delimited with ITEM_LONG_NAME, Site has SITE_CODE,
// Shelf has ARTICLE_CODE + LAYOUT_MODULE_CODE (but no SITE_CODE).
export const classifyJdaFileByHeader = (headerLine: string): JdaFileRole | null => {
  if (headerLine.includes("|")) {
    return headerLine.includes("ITEM_LONG_NAME") ? "articleMaster" : null;
  }
  if (headerLine.includes("SITE_CODE")) return "site";
  if (headerLine.includes("ARTICLE_CODE") && headerLine.includes("LAYOUT_MODULE_CODE")) {
    return "shelf";
  }
  return null;
};

const firstLineOf = (content: string): string => content.split(/\r?\n/, 1)[0] ?? "";

// Only the first line matters for classification — read a bounded prefix instead of the whole
// file via file.text(), which would materialize a large renamed JDA export entirely in memory
// just to inspect its header row.
const HEADER_SNIFF_BYTES = 4096;

export const classifyJdaFile = async (file: File): Promise<JdaFileRole | null> =>
  classifyJdaFileByName(file.name) ??
  classifyJdaFileByHeader(firstLineOf(await file.slice(0, HEADER_SNIFF_BYTES).text()));

// Buckets files into their 3 roles. Manually-assigned roles (isManual[i] = true)
// claim their slot before auto-detected ones, so overriding a misclassified
// file bumps whatever was auto-assigned there back to "unclassified" instead
// of silently keeping the wrong file in place.
export const assignFilesToRoles = (
  files: File[],
  roles: (JdaFileRole | null)[],
  isManual: boolean[],
): JdaFileBuckets => {
  const buckets: JdaFileBuckets = {
    site: null,
    shelf: null,
    articleMaster: null,
    unclassified: [],
  };

  const order = files
    .map((_, index) => index)
    .sort((a, b) => Number(isManual[b]) - Number(isManual[a]));

  for (const index of order) {
    const role = roles[index];
    if (role && !buckets[role]) {
      buckets[role] = files[index];
    } else {
      buckets.unclassified.push(files[index]);
    }
  }

  return buckets;
};

export const jdaFileKey = (file: File): string => `${file.name}-${file.size}`;
